import { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { persistOrgSubdomainFromCurrentUrl } from "lib/organizationType";
import TokenService from "services/token.service";
import { api } from "services/api.service";
import { AuthenticationContext } from "hooks/useAuthentication";

/** Relative in-app path only; prevents open redirects via ?next= */
function readSafeNextPath(): string | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("next") || params.get("return_path");
  if (!raw) return null;
  const decoded = decodeURIComponent(raw.trim());
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
  if (decoded.includes("://")) return null;
  return decoded;
}

function mapSupportError(data: { error?: string; code?: string } | undefined): string {
  const code = data?.code;
  if (code === "expired") {
    return "This impersonation link has expired. Request a new one from the staff app.";
  }
  if (code === "already_used") {
    return "This impersonation link was already used. Request a new one from the staff app.";
  }
  if (code === "invalid_session") {
    return "This impersonation link is invalid. Request a new one from the staff app.";
  }
  return data?.error || "Could not start support session.";
}

function Support() {
  const params = useParams<{ subdomain?: string; token?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setLoggedIn } = useContext(AuthenticationContext);
  const [error, setError] = useState<string | null>(null);

  const impersonationSessionIdFromQuery = searchParams.get("impersonation_session_id");
  const pathSubdomain = params.subdomain;
  const pathToken = params.token;

  useEffect(() => {
    persistOrgSubdomainFromCurrentUrl();

    let sessionIdentifier: string | null = null;
    let payload:
      | { session: { impersonation_session_id: string } }
      | { session: { support_token: string } }
      | null = null;

    if (impersonationSessionIdFromQuery) {
      sessionIdentifier = impersonationSessionIdFromQuery;
      payload = { session: { impersonation_session_id: impersonationSessionIdFromQuery } };
    } else if (pathSubdomain && pathToken) {
      sessionIdentifier = pathToken;
      payload = { session: { impersonation_session_id: pathToken } };
    } else if (pathToken) {
      sessionIdentifier = pathToken;
      payload = { session: { support_token: pathToken } };
    }

    if (!sessionIdentifier || !payload) {
      setError("Missing impersonation session.");
      return;
    }

    const delayMs = 50;
    const timeout = window.setTimeout(() => {
      const guardKey = `support_session_exchange_${sessionIdentifier}`;
      if (sessionStorage.getItem(guardKey) === "1") return;
      sessionStorage.setItem(guardKey, "1");

      api
        .post("/start_support_session", payload)
        .then(({ data }) => {
          const jwt = data?.jwt as string | undefined;
          if (jwt) {
            TokenService.set(jwt);
            setLoggedIn(true);
            const nextPath = readSafeNextPath();
            navigate(nextPath || "/", { replace: true });
            return;
          }
          sessionStorage.removeItem(guardKey);
          setError(mapSupportError(data as { error?: string; code?: string }));
        })
        .catch((err: { response?: { data?: { error?: string; code?: string } } }) => {
          sessionStorage.removeItem(guardKey);
          setError(mapSupportError(err?.response?.data));
        });
    }, delayMs);

    return () => window.clearTimeout(timeout);
  }, [
    impersonationSessionIdFromQuery,
    pathSubdomain,
    pathToken,
    navigate,
    setLoggedIn,
  ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      gap={2}
      px={2}
    >
      {error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Signing you in…
          </Typography>
        </>
      )}
    </Box>
  );
}

export default Support;
